import React, { useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCompany } from '../context/CompanyContext';
import * as authService from '../services/auth.service';
import Sidebar from './Sidebar';
import { FaSignOutAlt, FaUserCircle, FaCog, FaTimes } from 'react-icons/fa';

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, logout: authLogout } = useAuth();
  const { company } = useCompany();

  const [showSettings, setShowSettings] = useState(false);
  const [userName, setUserName] = useState(() => {
    return localStorage.getItem('userDisplayName') || user?.name || 'User';
  });
  const [userSettingsForm, setUserSettingsForm] = useState(userName);

  const handleLogout = async () => {
    try {
      await authService.logout();
      authLogout();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleSaveUserName = () => {
    localStorage.setItem('userDisplayName', userSettingsForm);
    setUserName(userSettingsForm);
    setShowSettings(false);
  };

  const handleClearCache = () => {
    if (window.confirm('Are you sure you want to clear all cached data?')) {
      localStorage.removeItem('clientCatalogs');
      localStorage.removeItem('products');
      alert('Cache cleared successfully!');
    }
  };

  return (
    <div className="min-vh-100 bg-light">
      <nav className="navbar navbar-expand-lg navbar-dark shadow-lg navbar-gradient">
        <div className="container-fluid px-4">
          <div className="navbar-brand ec360-brand d-flex align-items-center">
            <div className="ec360-logo">EC360</div>

            <div className="ec360-brand-text">
              <div className="ec360-title">
                EnterpriseCatalog 360
              </div>
              <div className="ec360-subtitle">
                Lifecycle ERP Platform
              </div>
            </div>
          </div>

          {/* Company Logo and Name - Center */}
          {company && company.companyLogo && company.companyName && (
            <div className="navbar-company-center d-flex align-items-center justify-content-center flex-grow-1">
              <div className="company-logo-badge d-flex align-items-center gap-2">
                <img 
                  src={company.companyLogo} 
                  alt={company.companyName}
                  className="company-logo-img"
                  title={company.companyName}
                />
                <span className="company-name-text">{company.companyName}</span>
              </div>
            </div>
          )}
          
          <button
            className="navbar-toggler navbar-toggler-custom"
            type="button"
            data-bs-toggle="collapse"
            data-bs-target="#navbarContent"
            aria-controls="navbarContent"
            aria-expanded="false"
            aria-label="Toggle navigation"
          >
            <span className="navbar-toggler-icon"></span>
          </button>
          <div className="collapse navbar-collapse" id="navbarContent">
            <div className="ms-auto d-flex align-items-center gap-3">
              <div className="d-flex align-items-center nav-user-info">
                <FaUserCircle color="#fff" size={24} className="me-2 nav-user-avatar" />
                <div>
                  <div className="nav-user-greeting">Welcome back</div>
                  <div className="nav-user-name">{userName}</div>
                </div>
              </div>
              <div className="nav-divider"></div>
              <button
                onClick={() => setShowSettings(true)}
                className="btn btn-light nav-action-btn d-flex align-items-center justify-content-center"
                title="Settings"
              >
                <FaCog size={18} />
              </button>
              <button
                onClick={handleLogout}
                className="btn btn-light nav-action-btn"
              >
                <FaSignOutAlt className="me-2" size={16} />
                Logout
              </button>
            </div>
          </div>
        </div>
      </nav>

      <div>
        <main>
          <div className="container-fluid">
            <div className="row main-layout-row">
              <div className="col-auto p-0">
                <Sidebar />
              </div>
              <div className="col main-content-col">
                <div className="h-100">
                  <Outlet />
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>

      {showSettings && (
        <div className="settings-overlay d-flex align-items-center justify-content-center">
          <div className="settings-modal d-flex flex-column">
            <div className="settings-modal-header d-flex justify-content-between align-items-center">
              <h5 className="fw-semibold text-dark mb-0">Settings</h5>
              <button
                onClick={() => { setShowSettings(false); setUserSettingsForm(userName); }}
                className="btn-close-custom"
              >
                <FaTimes />
              </button>
            </div>

            <div className="settings-modal-body flex-grow-1 overflow-auto d-flex flex-column gap-4">
              <div className="settings-section rounded-3 p-3 bg-light d-flex flex-column gap-3">
                <h6 className="fw-semibold text-dark mb-0 settings-section-title">User Profile</h6>
                <div className="d-flex flex-column gap-2">
                  <label className="fw-medium text-dark mb-0 settings-label">Display Name</label>
                  <div className="d-flex gap-2">
                    <input
                      type="text"
                      value={userSettingsForm}
                      onChange={(e) => setUserSettingsForm(e.target.value)}
                      placeholder="Enter your name"
                      className="form-control settings-input"
                    />
                    <button onClick={handleSaveUserName} className="btn btn-primary settings-save-btn fw-semibold">
                      Save
                    </button>
                  </div>
                </div>
              </div>

              <div className="settings-section rounded-3 p-3 bg-light d-flex flex-column gap-3">
                <h6 className="fw-semibold text-dark mb-0 settings-section-title">Cache Management</h6>
                <p className="text-muted small mb-0">Clear all cached data including catalogs and products</p>
                <button onClick={handleClearCache} className="btn btn-outline-danger fw-semibold settings-cache-btn align-self-start">
                  Clear Cache
                </button>
              </div>
            </div>

            <div className="settings-modal-footer d-flex justify-content-end gap-3">
              <button
                onClick={() => { setShowSettings(false); setUserSettingsForm(userName); }}
                className="btn btn-outline-secondary fw-semibold"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
